import React, { useEffect, useMemo, useState } from "react";
import { Grid, Typography } from "@mui/material";
import EntityHubLayout, { PAGE_SIZE } from "../../components/product-manager/EntityHubLayout";
import EntityAssetCard from "../../components/product-manager/EntityAssetCard";
import EditOperationalErrorDialog from "../../components/product-manager/EditOperationalErrorDialog";
import BulkEntityUploadDialog from "../../components/product-manager/BulkEntityUploadDialog";
import { OPERATIONAL_ERROR_BULK_CONFIG } from "../../utils/bulkUpload/bulkEntityConfigs";
import {
  filterByHubState,
  useBookmarkableHubState,
} from "../../hooks/useBookmarkableHubState";
import { useOperationalErrors } from "../../hooks/useOperationalErrors";
import { apiService } from "../../api/services/apiService";
import type { OperationalError } from "../../types";
import { EntityStatus } from "../../types";
import { pmHubAddPath } from "../../utils/pmHubRoutes";

const OperationalErrorHubPage: React.FC = () => {
  const hubState = useBookmarkableHubState();
  const { errors: fetched, loading, error, refresh } = useOperationalErrors();
  const [errors, setErrors] = useState<OperationalError[]>([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [toggleLoadingId, setToggleLoadingId] = useState<string | null>(null);
  useEffect(() => {
    setErrors(fetched);
  }, [fetched]);

  const filtered = useMemo(
    () =>
      filterByHubState(errors, hubState, undefined, (e) => [
        e.code ?? "",
        e.name,
        e.message ?? "",
        e.description ?? "",
        ...(e.errorStrings ?? []),
      ]),
    [errors, hubState],
  );

  const paginated = useMemo(() => {
    const start = (hubState.page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, hubState.page]);

  const editItem = useMemo(
    () => errors.find((e) => e.uuid === hubState.editUuid) ?? null,
    [errors, hubState.editUuid],
  );

  const handleToggle = async (item: OperationalError) => {
    setToggleLoadingId(item.uuid);
    const newStatus =
      item.status === EntityStatus.ACTIVE
        ? EntityStatus.INACTIVE
        : EntityStatus.ACTIVE;
    try {
      const updated = await apiService.updateOperationalError(item.uuid, {
        status: newStatus,
      });
      setErrors((prev) => prev.map((e) => (e.uuid === updated.uuid ? updated : e)));
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to update status");
    } finally {
      setToggleLoadingId(null);
    }
  };

  return (
    <>
      <EntityHubLayout
        hubTitle="Operational Errors"
        hubDescription="Curate error codes, messages, and log strings for the Production Support Operator Hub. Create individually or via bulk upload."
        addPath={pmHubAddPath("operationalError")}
        addLabel="Add error"
        onBulkUpload={() => setBulkOpen(true)}
        loading={loading}
        error={error}
        itemCount={filtered.length}
        hubState={hubState}
        filterSidebarExtra={
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            Used by CSR operators for MTTR and SOP extraction.
          </Typography>
        }
      >
        <Grid container spacing={2}>
          {paginated.map((err) => (
            <Grid item xs={12} sm={6} md={4} key={err.uuid}>
              <EntityAssetCard
                title={err.code ?? err.name}
                subtitle={err.message ?? err.description ?? ""}
                status={err.status}
                enabled={err.status === EntityStatus.ACTIVE}
                toggleLoading={toggleLoadingId === err.uuid}
                onToggleEnabled={() => handleToggle(err)}
                onEdit={() => hubState.setEditUuid(err.uuid)}
                onDelete={async () => {
                  await apiService.deleteOperationalError(err.uuid);
                  setErrors((prev) => prev.filter((e) => e.uuid !== err.uuid));
                }}
              />
            </Grid>
          ))}
          {!loading && filtered.length === 0 && (
            <Grid item xs={12}>
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                No operational errors match your filters.
              </Typography>
            </Grid>
          )}
        </Grid>
      </EntityHubLayout>

      <BulkEntityUploadDialog
        open={bulkOpen}
        config={OPERATIONAL_ERROR_BULK_CONFIG}
        onClose={() => setBulkOpen(false)}
        onComplete={() => refresh()}
      />

      <EditOperationalErrorDialog
        item={editItem}
        open={!!editItem}
        onClose={() => hubState.setEditUuid(null)}
        onSaved={(updated) => {
          setErrors((prev) => prev.map((e) => (e.uuid === updated.uuid ? updated : e)));
          hubState.setEditUuid(null);
        }}
      />
    </>
  );
};

export default OperationalErrorHubPage;
